import React, { useEffect, useState } from 'react';
import Hyperlink from 'react-native-hyperlink';
import {
  getBrandColor,
  getBrandName,
  isBND,
  isTLA,
  LocalizePropType,
  isKYCApp,
  selectIsKeyboardOpened,
  isFRM,
  isGRF,
  isLocalizeAdded,
  UserDataOutput,
  isSCT,
  isOSN,
  selectBrandSettings,
  DEFAULT_PASSWORD_REGEX,
  usePasswordConditions,
  isUBQ,
} from 'getaway-data-layer';
import './SignupView.less';
import { FormikValues } from 'formik';
import Box from '@mui/material/Box';
import {
  IconButton,
  InputAdornment,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Flag from 'react-world-flags';
import BrandButton from '../../components/BrandButton/BrandButton';
import NavigationBar from '../../components/NavigationBar/NavigationBar';
import config from '../../../env.config';
import { GA_TITLES, isMobileBrowser } from '../../../utils';
import { LEGAL_TYPES, SIGNUP_TYPES } from '../../../validation';
import { useSelector } from 'react-redux';
import { Checkbox } from 'antd';
import { PRIVACY_POLICY_STATUS } from '../../../enums/PrivacyPolicy';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';
import remarkGfm from 'remark-gfm';
import ListItem from '../../components/ListItem/ListItem';
import CountryRadioSelector from '../../components/CountryRadioSelector/CountryRadioSelector';

type props = {
  localize: LocalizePropType;
  formik: FormikValues;
  signupType: string;
  showPassword: boolean;
  onClose: () => void;
  showAboutDialog: () => void;
  handleClickShowPassword: () => void;
  handleMouseDownPassword: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onChangeSignupType: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onChangeLegalType: (event: React.MouseEvent<HTMLButtonElement>) => void;
  containerStyles: any;
  showBack: boolean;
  onPressLogin: () => void;
  showLoginLink: boolean;
  privacyPolicyStatus: string;
  privacyPolicyConsentRequired: boolean;
  onPressPrivacyPolicyConsent: () => void;
  handleCompanyNameChange: (event: React.MouseEvent<HTMLButtonElement>) => void;
  turnstileRef: any;
  isWidget: boolean;
  user: UserDataOutput;
  magicLink: string;
  onPressCopyLink: () => void;
  openMagicLink: () => void;
  resetCountdown: number;
  onPressReset: () => void;
  showInvitationBox: boolean;
  invitedByName: string;
  isEmailMissTyped: boolean;
  correctedEmail: string;
  handleEmailChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onCountryValueChange: (arg: any) => void;
  showCountryRadio?: boolean;
  isDeUbqShutdown?: boolean;
  formattedUbqShutdownDate?: string;
  onShutdownCTA: () => void;
  isTurnstileLoading?: boolean;
};

const SignupView = ({
  localize,
  formik,
  signupType,
  showPassword,
  onClose,
  showAboutDialog,
  handleClickShowPassword,
  handleMouseDownPassword,
  onChangeSignupType,
  onChangeLegalType,
  containerStyles,
  showBack,
  onPressLogin,
  showLoginLink,
  privacyPolicyStatus,
  privacyPolicyConsentRequired,
  onPressPrivacyPolicyConsent,
  handleCompanyNameChange,
  turnstileRef,
  isWidget,
  user,
  magicLink,
  onPressCopyLink,
  openMagicLink,
  resetCountdown,
  onPressReset,
  showInvitationBox,
  invitedByName,
  isEmailMissTyped,
  correctedEmail,
  handleEmailChange,
  onCountryValueChange,
  showCountryRadio,
  isDeUbqShutdown,
  formattedUbqShutdownDate,
  onShutdownCTA,
  isTurnstileLoading = false,
}: props) => {
  const countryList = [
    { label: localize('DE'), code: 'DE' },
    { label: localize('AT'), code: 'AT' },
  ];
  const brandSettings = useSelector(selectBrandSettings);

  const activateReset = resetCountdown <= 0;
  const isKeyboardOpened = useSelector(selectIsKeyboardOpened) as any;
  const [local, domain] = correctedEmail.split('@');

  const passwordRegex =
    brandSettings?.passwordRegex || DEFAULT_PASSWORD_REGEX.toString();

  const { conditions } = usePasswordConditions(
    formik.values.password,
    passwordRegex,
    localize
  );

  const passwordConditionsValid =
    conditions?.length > 0 &&
    conditions.every((condition) => condition.test === true);

  const orgForm = (
    <>
      <TextField
        required
        fullWidth
        id="companyName"
        sx={{ pr: 1 }}
        label={localize('signup.company.name.placeholder')}
        helperText={
          (formik.touched.companyName && formik.errors.companyName) || ''
        }
        error={formik.touched.companyName && Boolean(formik.errors.companyName)}
        variant="standard"
        size="small"
        type="text"
        {...formik.getFieldProps('companyName')}
        onChange={handleCompanyNameChange}
      />
      <FormControl fullWidth variant="standard">
        <InputLabel id="legalType-label">
          {localize('signup.legal.type.placeholder')}
        </InputLabel>
        <Select
          fullWidth
          required
          id="legalType"
          variant="standard"
          {...formik.getFieldProps('legalType')}
          onChange={(event: any) => {
            onChangeLegalType(event);
          }}
          style={{ margin: '10px 0' }}
          inputProps={{
            label: localize('signup.legal.type.placeholder'),
          }}
          label={localize('signup.legal.type.placeholder')}
        >
          {Object.values(LEGAL_TYPES)?.map((typ) => (
            <MenuItem key={typ} value={typ}>
              {typ}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {formik.values.legalType === LEGAL_TYPES.Sonstige && (
        <>
          <TextField
            required
            fullWidth
            id="otherLegalType"
            sx={{ pr: 1 }}
            label={localize('signup.legal.other.placeholder')}
            helperText={
              (formik.touched.otherLegalType && formik.errors.otherLegalType) ||
              ''
            }
            error={
              formik.touched.otherLegalType &&
              Boolean(formik.errors.otherLegalType)
            }
            variant="standard"
            size="small"
            type="text"
            {...formik.getFieldProps('otherLegalType')}
          />
          {isLocalizeAdded(localize, 'signup.org.other.footnote') && (
            <div className="other-footnote">
              <Markdown rehypePlugins={[rehypeRaw]}>
                {localize('signup.org.other.footnote')}
              </Markdown>
            </div>
          )}
        </>
      )}
      <TextField
        required={formik.values.legalType !== LEGAL_TYPES.Sonstige}
        id="registerNr"
        sx={{ width: 0.5, pr: 1 }}
        label={localize('signup.register.nr.placeholder')}
        helperText={
          (formik.touched.registerNr && formik.errors.registerNr) || ''
        }
        error={formik.touched.registerNr && Boolean(formik.errors.registerNr)}
        variant="standard"
        size="small"
        type="text"
        {...formik.getFieldProps('registerNr')}
      />
      <TextField
        required={formik.values.legalType !== LEGAL_TYPES.Sonstige}
        id="registerLocation"
        sx={{ width: 0.5 }}
        label={localize('signup.register.location.placeholder')}
        helperText={
          (formik.touched.registerLocation && formik.errors.registerLocation) ||
          ''
        }
        error={
          formik.touched.registerLocation &&
          Boolean(formik.errors.registerLocation)
        }
        variant="standard"
        size="small"
        type="text"
        {...formik.getFieldProps('registerLocation')}
      />

      <div className="signup-section">
        {localize('signup.personal.section')}
      </div>
    </>
  );

  return (
    <div
      data-testid="SignupView-widgetContainer"
      className="screen-container"
      style={isWidget ? { display: 'initial' } : containerStyles}
    >
      {!isWidget ? (
        <NavigationBar
          TitleTestId="SignupView-title"
          DescriptionTestId="SignupView-description"
          title={localize(
            showInvitationBox
              ? 'signup.invitation.header'
              : signupType === SIGNUP_TYPES.ORG
              ? 'signup.org.header'
              : 'signup.header'
          )}
          description={localize(
            signupType === SIGNUP_TYPES.ORG ? 'signup.org.desc' : 'signup.desc'
          )}
          showHelp
          onClose={onClose}
          showLogo
          showBack={showBack}
          showLanguageSelector
        />
      ) : (
        <div style={{ position: 'absolute', visibility: 'hidden' }}>
          <LanguageSelector />
        </div>
      )}
      <div
        className="screen-body"
        style={
          isWidget ? { flex: 'auto', height: 'auto', overflowY: 'hidden' } : {}
        }
      >
        {showInvitationBox && (
          <>
            <div className="signup-invitation-box">
              <div>{localize('signup.invitation.prefix')}</div>
              <strong>{invitedByName}</strong>
            </div>
            <div className="signup-invitation-footnote">
              <div className="signup-invitation-footnote-text">
                {localize('signup.invitation.footnote')}
              </div>
            </div>
          </>
        )}
        {isWidget && user ? (
          <div
            data-testid="SignupView-successWidget"
            className="signup-success-widget"
          >
            <div className="signup-success-widget-container">
              <img
                src="/images/check-mark.png"
                alt="check"
                className="signup-success-widget-tick"
              />
              <Markdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
                {localize('signup.md.widget.success')}
              </Markdown>
            </div>
            <div>
              <BrandButton
                id={GA_TITLES.AUTH.SIGN_UP}
                title={localize('signup.widget.success.cta')}
                containerStyles={{ position: 'unset', width: '100%' }}
                enableKeyboardEffect={false}
                onClick={openMagicLink}
              />
              <ListItem
                title={magicLink}
                leftIcon="link"
                leftIconSize={22}
                rightIcon="copy"
                value={localize('web.copy')}
                brandValueColor
                rightIconSize={17}
                onClick={onPressCopyLink}
              />

              <div
                data-testid="SignupView-resetBtn"
                className="signup-widget-reset-container"
                onClick={onPressReset}
              >
                <div className={'signup-widget-reset-active'}>
                  {activateReset
                    ? localize('signup.widget.success.reset')
                    : `${localize(
                        'signup.widget.success.reset'
                      )} (${resetCountdown}s)`}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {showCountryRadio && isUBQ() && !isWidget && (
              <div style={{ marginBottom: 10 }}>
                <div className="signup-country-label">
                  {localize('signup.country.label')}
                </div>
                <CountryRadioSelector
                  options={countryList}
                  onChange={onCountryValueChange}
                  value="DE"
                />
              </div>
            )}
            {isDeUbqShutdown && (
              <div
                className="signup-account-warning"
                data-testid="signup-migratedDialog"
              >
                <div className="signup-account-warning-title">
                  {localize('signup.shutdown.title')}
                </div>
                <div className="signup-account-warning-msg">
                  {localize(
                    'signup.shutdown.description',
                    formattedUbqShutdownDate
                  )}
                </div>

                <div className="signup-account-warning-row">
                  <button
                    className="signup-account-warning-btn"
                    onClick={onShutdownCTA}
                  >
                    {localize('signup.shutdown.button')}
                  </button>
                </div>
              </div>
            )}
            {!isDeUbqShutdown && (
              <Box
                data-testid="SignupView-signupForm"
                component="form"
                noValidate
                onSubmit={formik.handleSubmit}
              >
                <>
                  {!showInvitationBox &&
                    signupType !== SIGNUP_TYPES.ORG &&
                    isKYCApp() &&
                    (isBND() ||
                      isTLA() ||
                      isFRM() ||
                      isGRF() ||
                      isOSN() ||
                      isSCT()) && (
                      <Select
                        data-testid="SignupView-signupType"
                        fullWidth
                        required
                        labelId="select-label"
                        id="signupType"
                        variant="standard"
                        {...formik.getFieldProps('signupType')}
                        onChange={(event: any) => {
                          onChangeSignupType(event);
                        }}
                      >
                        <MenuItem value={SIGNUP_TYPES.PRIVATE}>
                          {localize('signup.type.private')}
                        </MenuItem>
                        {(isBND() || isGRF() || isOSN() || isSCT()) && (
                          <MenuItem
                            data-testid="SignupView-companyType"
                            value={SIGNUP_TYPES.COMPANY}
                          >
                            {localize('signup.type.company')}
                          </MenuItem>
                        )}
                        {isFRM() && (
                          <MenuItem value={SIGNUP_TYPES.CO_USER}>
                            {localize('signup.type.co_user')}
                          </MenuItem>
                        )}
                        {isFRM() && (
                          <MenuItem value={SIGNUP_TYPES.BIKE}>
                            {localize('signup.type.bike')}
                          </MenuItem>
                        )}
                        {isTLA() && (
                          <MenuItem
                            data-testid="SignupView-companyType"
                            value={SIGNUP_TYPES.ORG}
                          >
                            {localize('signup.type.org')}
                          </MenuItem>
                        )}
                        {(isTLA() || isOSN()) && (
                          <MenuItem value={SIGNUP_TYPES.PARTNER}>
                            {localize('signup.type.partner')}
                          </MenuItem>
                        )}
                        {isOSN() && (
                          <MenuItem value={SIGNUP_TYPES.ALTERNATIVE}>
                            {localize('signup.type.alternative')}
                          </MenuItem>
                        )}
                      </Select>
                    )}
                  {signupType === SIGNUP_TYPES.ORG && orgForm}
                  <TextField
                    data-testid="SignupView-firstName"
                    required
                    id="firstName"
                    sx={{ width: 0.5, pr: 1 }}
                    label={localize('signup.name.placeholder')}
                    helperText={
                      (formik.touched.firstName && formik.errors.firstName) ||
                      ''
                    }
                    error={
                      formik.touched.firstName &&
                      Boolean(formik.errors.firstName)
                    }
                    variant="standard"
                    size="small"
                    type="text"
                    autoComplete="given-name"
                    {...formik.getFieldProps('firstName')}
                  />
                  <TextField
                    data-testid="SignupView-lastName"
                    required
                    id="lastName"
                    sx={{ width: 0.5 }}
                    label={localize('signup.facebook.lastname.placeholder')}
                    helperText={
                      (formik.touched.lastName && formik.errors.lastName) || ''
                    }
                    error={
                      formik.touched.lastName && Boolean(formik.errors.lastName)
                    }
                    variant="standard"
                    size="small"
                    type="text"
                    autoComplete="family-name"
                    {...formik.getFieldProps('lastName')}
                  />
                  <TextField
                    data-testid="SignupView-email"
                    required
                    fullWidth
                    id="email"
                    className="form-input"
                    label={localize(
                      signupType === SIGNUP_TYPES.ORG
                        ? 'signup.email.org.placeholder'
                        : 'signup.email.placeholder'
                    )}
                    helperText={
                      formik.touched.email && formik.errors.email ? (
                        formik.errors.email
                      ) : isEmailMissTyped &&
                        !(formik.errors.email && formik.touched.email) ? (
                        <>
                          {localize('email.domain.error.prefix')} {local}@
                          <strong>{domain}</strong> ?
                        </>
                      ) : (
                        ''
                      )
                    }
                    error={
                      (formik.touched.email && Boolean(formik.errors.email)) ||
                      isEmailMissTyped
                    }
                    variant="standard"
                    size="small"
                    type="email"
                    autoComplete="email"
                    {...formik.getFieldProps('email')}
                    onChange={handleEmailChange}
                    sx={
                      isEmailMissTyped
                        ? {
                            '& label': {
                              color: 'red',
                            },
                            '& label.Mui-focused': { color: 'red' },
                            '& .MuiInput-underline:after': {
                              borderBottomColor: 'red',
                            },
                            '& .MuiInput-underline:before': {
                              borderBottomColor: 'red',
                            },
                          }
                        : undefined
                    }
                  />
                  <TextField
                    data-testid="SignupView-password"
                    required
                    fullWidth
                    id="password"
                    className="form-input"
                    type={showPassword ? 'text' : 'password'}
                    label={localize('signup.password.placeholder')}
                    helperText={
                      formik.values.password.length < 1
                        ? formik.touched.password && formik.errors.password
                        : !passwordConditionsValid &&
                          conditions.map((condition, i) => (
                            <div key={i} className="signup-password-conditions">
                              <span>
                                <img
                                  src={
                                    condition.test
                                      ? '/images/correct.svg'
                                      : '/images/wrong.svg'
                                  }
                                  style={{ width: 14, height: 14 }}
                                />
                              </span>
                              <span
                                style={{
                                  color: condition.test ? 'green' : 'red',
                                  marginLeft: '4px',
                                }}
                              >
                                {condition.message}
                              </span>
                            </div>
                          ))
                    }
                    error={
                      formik.touched.password && Boolean(formik.errors.password)
                    }
                    variant="standard"
                    size="small"
                    autoComplete="new-password"
                    {...formik.getFieldProps('password')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <div
                    className="signup-consent-container"
                    onClick={
                      privacyPolicyConsentRequired
                        ? onPressPrivacyPolicyConsent
                        : null
                    }
                  >
                    {privacyPolicyConsentRequired && (
                      <Checkbox
                        data-testid="SignupView-privacyConsent"
                        style={{ marginRight: 8 }}
                        checked={
                          privacyPolicyStatus === PRIVACY_POLICY_STATUS.APPROVED
                        }
                      />
                    )}
                    <div
                      className={
                        privacyPolicyStatus === PRIVACY_POLICY_STATUS.ERROR
                          ? 'signup-privacy-policy-container-error'
                          : 'signup-privacy-policy-container'
                      }
                    >
                      {isLocalizeAdded(localize, 'signup.terms.text.md') ||
                      isLocalizeAdded(localize, 'signup.terms.text.org.md') ? (
                        <Markdown rehypePlugins={[rehypeRaw]}>
                          {localize(
                            signupType === SIGNUP_TYPES.ORG &&
                              isLocalizeAdded(
                                localize,
                                'signup.terms.text.org.md'
                              )
                              ? 'signup.terms.text.org.md'
                              : 'signup.terms.text.md'
                          )}
                        </Markdown>
                      ) : (
                        <Hyperlink
                          linkStyle={{ color: getBrandColor() }}
                          linkText={(url) => {
                            if (url === localize('signup.terms.url')) {
                              return localize('signup.terms.url.text');
                            }
                            if (url === localize('privacy.url')) {
                              return localize('signup.name.policy.text');
                            }
                            return url;
                          }}
                          onPress={showAboutDialog}
                        >
                          <div
                            style={
                              privacyPolicyStatus ===
                              PRIVACY_POLICY_STATUS.ERROR
                                ? { color: 'red' }
                                : {}
                            }
                          >
                            {localize(
                              'signup.terms.text',
                              localize('signup.terms.url'),
                              localize('privacy.url'),
                              localize(
                                `brand.${getBrandName(
                                  config.brand.xBrandId!
                                )}.name`
                              )
                            )}
                          </div>
                        </Hyperlink>
                      )}
                    </div>
                  </div>
                  <div ref={turnstileRef} className="turnstile-widget" />
                  {!isKeyboardOpened && (
                    <div
                      className="screen-vertical-space"
                      style={isWidget ? { height: 70 } : {}}
                    />
                  )}
                  <div
                    className="signup-footnote-container"
                    style={
                      isKeyboardOpened && isMobileBrowser()
                        ? {
                            position: 'unset',
                            width: '100%',
                            marginTop: 30,
                            padding: 0,
                          }
                        : {}
                    }
                  >
                    <BrandButton
                      id={GA_TITLES.AUTH.SIGN_UP}
                      title={localize('signup.button.title')}
                      containerStyles={{ position: 'unset', width: '100%' }}
                      formik={formik}
                      enableKeyboardEffect={!isWidget}
                      disabled={isEmailMissTyped || isTurnstileLoading}
                    />
                    {!isWidget ? (
                      <div onClick={onPressLogin} className="signup-footnote">
                        <div className="signup-footnote-text">
                          {localize('signup.footnote.login.text')}
                        </div>
                        <div
                          data-testid="SignupView-footnoteLogin"
                          className="signup-footnote-action"
                        >
                          {localize('signup.footnote.login.action')}
                        </div>
                      </div>
                    ) : (
                      <div className="signup-secure-footnote">
                        <img
                          src="/images/lock.png"
                          className="signup-footnote-secure-icon"
                          alt="alt"
                        />
                        <div className="signup-footnote-secure-text">
                          {localize('signup.footnote.secure.text')}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              </Box>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SignupView;
